'use client';

import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, ShieldCheck, MapPin, Phone, Briefcase, Mail, ExternalLink } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { getAgencyProfile } from '@/doi-tac-module/actions/agency-actions';
import { getAgencyName } from '@/doi-tac-module/lib/agency-utils';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { allIndustries } from '@/doi-tac-module/lib/industry-data';
import { industryTranslations } from '@/doi-tac-module/constants/industry-translations';
import { AgencyProfile } from '@/doi-tac-module/lib/types';
import { AgencyContactButtons } from './agency-contact-buttons';
import { AgencyAvatarHover } from './agencyAvatar_hover';

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMediaQuery } from '@/doi-tac-module/hooks/use-media-query';

interface AgencyLogoHoverProps {
    agencyId: string;
    initialData?: AgencyProfile | null;
    language: string;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    showName?: boolean;
    nameClassName?: string;
    trigger?: React.ReactNode;
    side?: 'top' | 'bottom' | 'left' | 'right';
    align?: 'start' | 'center' | 'end';
    showProfileFooter?: boolean;
    layout?: 'logo' | 'avatar';
}

export function AgencyLogoHover({
    agencyId,
    initialData,
    language,
    size = 'sm',
    showName = false,
    nameClassName = "font-semibold text-right leading-snug",
    trigger,
    side = 'top',
    align = 'center',
    showProfileFooter = false,
    layout = 'logo'
}: AgencyLogoHoverProps) {
    const [agencyDetail, setAgencyDetail] = useState<AgencyProfile | null>(initialData || null);
    const [isLoading, setIsLoading] = useState(!initialData && !!agencyId);

    // Detect mobile device
    const isMobile = useMediaQuery("(max-width: 768px)");

    const resolvedAgencyName = useMemo(() => {
        return agencyDetail?.companyName || getAgencyName(agencyId);
    }, [agencyDetail, agencyId]);

    const agencyLogo = useMemo(() => {
        if (!agencyDetail) return '';
        return typeof agencyDetail.companyLogo === 'string'
            ? agencyDetail.companyLogo
            : (agencyDetail.companyLogo as any)?.url || '';
    }, [agencyDetail]);

    const fetchAgencyDetails = useCallback(async (open: boolean) => {
        if (!open || agencyDetail || !agencyId || isLoading) return;

        setIsLoading(true);
        try {
            const res = await getAgencyProfile(agencyId);
            if (res.success && res.data) {
                setAgencyDetail(res.data);
            }
        } catch (error) {
            console.error('Failed to fetch agency details:', error);
        } finally {
            setIsLoading(false);
        }
    }, [agencyDetail, agencyId, isLoading]);

    // Effect to sync with initialData if it changes
    useEffect(() => {
        if (initialData) {
            setAgencyDetail(initialData);
            setIsLoading(false);
        }
    }, [initialData]);

    const getIndustryName = (slug: string | string[]) => {
        if (!slug) return '---';
        const actualSlug = Array.isArray(slug) ? slug[0] : slug;
        if (typeof actualSlug !== 'string') return '---';

        const found = allIndustries.find(i => i.slug?.toLowerCase() === actualSlug.toLowerCase());
        const nameToTranslate = found ? found.name : actualSlug;

        const translation = (industryTranslations as any)[nameToTranslate];
        if (translation) return translation[language as 'vi' | 'bn'] || nameToTranslate;

        return found ? found.name : (actualSlug.charAt(0).toUpperCase() + actualSlug.slice(1).replace(/-/g, ' '));
    };

    const sizeClasses = {
        xs: "h-6 w-6",
        sm: "h-8 w-8",
        md: "h-10 w-10",
        lg: "h-12 w-12"
    };

    const t = agencyTranslations as any;

    const CommonContent = (
        <>
            {/* Row 1: Logo & Company Name */}
            <div className="p-4 bg-slate-50/50 border-b border-slate-100 flex items-center gap-3">
                <Link href={`/cac-dai-ly/${agencyId}`} className="group flex items-center gap-3 w-full">
                    <div className="h-10 w-10 min-w-10 rounded-lg bg-white border border-slate-200 p-1 flex items-center justify-center shadow-sm overflow-hidden group-hover:border-primary/50 transition-colors">
                        {agencyLogo ? (
                            <img src={agencyLogo} alt="Logo" className="w-full h-full object-contain" />
                        ) : (
                            <Building2 className="h-5 w-5 text-slate-300" />
                        )}
                    </div>
                    <div className="flex flex-col">
                        <div className="flex items-center gap-1.5">
                            <h4 className="text-sm font-bold text-slate-900 leading-tight group-hover:text-primary transition-colors">
                                {resolvedAgencyName}
                            </h4>
                            {agencyDetail?.rlNumber && (
                                <div title={t.hover?.verified?.[language]}>
                                    <ShieldCheck className="h-4 w-4 text-[#FF6921]" fill="#FF692120" />
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                            <div className={`px-2 py-0.5 rounded text-[10px] font-bold text-white w-fit ${agencyDetail?.role === 'MASTER_CTV' ? 'bg-[#19A6DF]' : 'bg-[#F2B92A]'}`}>
                                {agencyDetail?.role === 'MASTER_CTV'
                                    ? t.hover?.master?.[language]
                                    : t.hover?.level1?.[language]}
                            </div>
                            {agencyDetail?.establishedYear && (
                                <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">
                                    • {t.hover?.established?.[language]} {agencyDetail.establishedYear}
                                </span>
                            )}
                        </div>
                    </div>
                </Link>
            </div>

            {/* Row 2: Info & Industry */}
            <div className="p-4 space-y-3">
                {agencyDetail?.rlNumber && (
                    <div className="flex items-start gap-3 p-2 rounded-lg bg-blue-50/50 border border-blue-100/50">
                        <ShieldCheck className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
                        <div className="flex flex-col text-left">
                            <span className="text-[10px] text-slate-400 font-medium">{t.hover?.rlNumber?.[language]}</span>
                            <span className="text-xs font-mono font-bold text-slate-700">{agencyDetail.rlNumber}</span>
                        </div>
                    </div>
                )}

                <div className="flex items-start gap-3">
                    <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex flex-col text-left">
                        <span className="text-[10px] text-slate-400 font-medium">{t.hover?.address?.[language]}</span>
                        <span className="text-xs text-slate-700 leading-snug">{agencyDetail?.officeAddress || (agencyDetail as any)?.location || '---'}</span>
                    </div>
                </div>

                {(agencyDetail?.officialPhone || (agencyDetail as any)?.phone) && (
                    <div className="flex items-center gap-3">
                        <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <div className="flex flex-col text-left">
                            <span className="text-[10px] text-slate-400 font-medium">{t.hover?.phone?.[language]}</span>
                            <span className="text-xs text-slate-700">{(agencyDetail?.officialPhone || (agencyDetail as any)?.phone)}</span>
                        </div>
                    </div>
                )}

                <div className="flex items-start gap-3">
                    <Briefcase className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex flex-col text-left">
                        <span className="text-[10px] text-slate-400 font-medium">{t.hover?.industry?.[language]}</span>
                        <span className="text-xs text-slate-700 leading-snug">
                            {(agencyDetail?.mainIndustries && (agencyDetail.mainIndustries as string[]).length > 0)
                                ? (agencyDetail.mainIndustries as string[]).map((ind: string) => getIndustryName(ind)).join(', ')
                                : (agencyDetail?.mainExpertiseI18n as any)?.[language] || (agencyDetail?.mainExpertise || '---')}
                        </span>
                    </div>
                </div>

                {(agencyDetail?.officialEmail || agencyDetail?.websiteUrl) && (
                    <div className="pt-1 flex flex-wrap gap-2">
                        {agencyDetail?.officialEmail && (
                            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 border border-slate-100 text-[10px] text-slate-600">
                                <Mail className="h-3 w-3 text-slate-400" />
                                {agencyDetail.officialEmail}
                            </div>
                        )}
                        {agencyDetail?.websiteUrl && (
                            <a
                                href={agencyDetail.websiteUrl.startsWith('http') ? agencyDetail.websiteUrl : `https://${agencyDetail.websiteUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 border border-blue-100 text-[10px] text-blue-600 hover:bg-blue-100 transition-colors"
                            >
                                <ExternalLink className="h-3 w-3" />
                                {t.hover?.website?.[language]}
                            </a>
                        )}
                    </div>
                )}
            </div>

            {/* Row 3: Representative */}
            {showProfileFooter ? (
                <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                        <AgencyContactButtons
                            agencyId={agencyId}
                            preloadedAgency={agencyDetail}
                            displayConsultant={agencyDetail}
                            language={language}
                            showChatText={false}
                            hideTrigger={true}
                        />
                    </div>
                    <Link href={`/cac-dai-ly/${agencyId}`} className="group flex flex-col text-left">
                        <span className="text-sm font-bold text-slate-800 mb-0.5 group-hover:text-primary transition-colors">
                            {agencyDetail?.managingDirector || agencyDetail?.representativeName || resolvedAgencyName}
                        </span>
                        <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">
                            {agencyDetail?.representativeTitle || (agencyDetail?.mainExpertiseI18n as any)?.[language] || agencyDetail?.mainExpertise || t.hover?.representative?.[language]}
                        </span>
                    </Link>
                </div>
            ) : (
                <div className="p-3 bg-slate-50 border-t border-slate-100 flex items-center gap-2.5">
                    <Link href={`/cac-dai-ly/${agencyId}`} className="group flex items-center gap-2.5 w-full">
                        <Avatar className="h-9 w-9 border border-white shadow-sm shrink-0 group-hover:border-primary/50 transition-colors">
                            <AvatarImage src={agencyDetail?.avatarUrl || (typeof agencyDetail?.representativePhoto === 'string' ? agencyDetail?.representativePhoto : (agencyDetail?.representativePhoto as any)?.url)} className="object-cover" />
                            <AvatarFallback className="bg-white text-slate-300 text-xs italic font-serif">
                                {(agencyDetail?.managingDirector || agencyDetail?.representativeName || resolvedAgencyName)?.charAt(0) || 'R'}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col text-left">
                            <span className="text-xs font-bold text-slate-900 leading-none mb-1 group-hover:text-primary transition-colors">
                                {agencyDetail?.managingDirector || agencyDetail?.representativeName || resolvedAgencyName}
                            </span>
                            <span className="text-[10px] text-slate-500 leading-none">
                                {t.hover?.representative?.[language]}
                            </span>
                        </div>
                    </Link>
                </div>
            )}
        </>
    );

    const commonTrigger = trigger || (
        <Avatar id="agencyLogo_hover" className={`${sizeClasses[size]} border border-slate-200 shadow-sm shrink-0 cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all p-1 bg-white`}>
            <AvatarImage src={agencyLogo} alt="Agency Logo" className="object-contain" />
            <AvatarFallback className="bg-white text-slate-300 text-[10px] font-bold font-serif">
                {resolvedAgencyName?.charAt(0) || 'A'}
            </AvatarFallback>
        </Avatar>
    );

    if (isMobile) {
        return (
            <div className="flex items-center gap-2">
                {showName && <span className={nameClassName}>{resolvedAgencyName}</span>}
                <Popover onOpenChange={fetchAgencyDetails}>
                    <PopoverTrigger asChild>
                        <div onClick={(e) => { e.stopPropagation(); }}>
                            {commonTrigger}
                        </div>
                    </PopoverTrigger>
                    <PopoverContent
                        id="agencyLogo_hover"
                        className="w-80 p-0 overflow-hidden border-[#F2B92A]/30 shadow-2xl bg-white rounded-xl z-50"
                        side={side}
                        align={align}
                    >
                        {layout === 'avatar' ? (
                            <AgencyAvatarHover
                                agencyId={agencyId}
                                agencyDetail={agencyDetail}
                                language={language}
                                resolvedAgencyName={resolvedAgencyName}
                            />
                        ) : CommonContent}
                    </PopoverContent>
                </Popover>
            </div>
        )
    }

    return (
        <div className="flex items-center gap-2">
            {showName && (
                <span className={nameClassName}>
                    {isLoading && !agencyDetail ? (
                        <span className="inline-block w-24 h-4 bg-slate-100 animate-pulse rounded" />
                    ) : (
                        resolvedAgencyName
                    )}
                </span>
            )}

            <HoverCard openDelay={200} onOpenChange={fetchAgencyDetails}>
                <HoverCardTrigger asChild>
                    {commonTrigger}
                </HoverCardTrigger>

                <HoverCardContent
                    id="agencyLogo_hover"
                    className="w-80 p-0 overflow-hidden border-[#F2B92A]/30 shadow-2xl bg-white rounded-xl z-50"
                    side={side}
                    align={align}
                >
                    {layout === 'avatar' ? (
                        <AgencyAvatarHover
                            agencyId={agencyId}
                            agencyDetail={agencyDetail}
                            language={language}
                            resolvedAgencyName={resolvedAgencyName}
                        />
                    ) : CommonContent}
                </HoverCardContent>
            </HoverCard>
        </div>
    );
}
